/**
 * OWNED BY C5. Service-worker registration and the update handshake.
 *
 * `registerServiceWorker()` is called once from `main.tsx`. Everything else here is
 * a tiny observable store so the shell (C1) can render an "Updated — tap to reload"
 * toast whenever *it* decides the moment is safe (F-701: never mid-transaction).
 * Nothing in this module reloads the page on its own.
 */
import { SKIP_WAITING_MESSAGE } from "./routes";

export interface SwUpdateState {
  /** A new service worker is installed and waiting to take over. */
  updateReady: boolean;
  /** The registration, once `register()` resolved. */
  registration: ServiceWorkerRegistration | null;
  /** True once a registration attempt finished (successfully or not). */
  registered: boolean;
  /** Registration error, if any (kept for diagnostics; never surfaced as a blocking UI). */
  error: Error | null;
}

export interface RegisterOptions {
  /** URL of the built worker. vite-plugin-pwa emits `/sw.js` at the site root. */
  swUrl?: string;
  scope?: string;
  /** How often to poll for a new build while the app is open. 0 disables polling. */
  checkIntervalMs?: number;
  /** Register even outside a production build (the dev server does not emit a worker). */
  force?: boolean;
}

const DEFAULT_CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

let state: SwUpdateState = { updateReady: false, registration: null, registered: false, error: null };
const listeners = new Set<(s: SwUpdateState) => void>();
let started = false;
let applying = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

function setState(patch: Partial<SwUpdateState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

export function getSwUpdateState(): SwUpdateState {
  return state;
}

/** Subscribe to update-ready changes. Returns an unsubscribe function. */
export function subscribeSwUpdate(listener: (s: SwUpdateState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function supported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function trackWaiting(registration: ServiceWorkerRegistration): void {
  if (registration.waiting && navigator.serviceWorker.controller) setState({ updateReady: true });

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      // `controller` is null on the very first install — that is not an "update".
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
        setState({ updateReady: true });
      }
    });
  });
}

/**
 * Registers the service worker. Safe to call more than once; safe to call in
 * environments without service workers (tests, older browsers — N-008).
 */
export function registerServiceWorker(options: RegisterOptions = {}): void {
  if (started) return;
  started = true;

  const isProd = typeof import.meta !== "undefined" && Boolean(import.meta.env?.PROD);
  if (!options.force && !isProd) {
    setState({ registered: true });
    return;
  }
  if (!supported()) {
    setState({ registered: true });
    return;
  }

  const swUrl = options.swUrl ?? "/sw.js";
  const scope = options.scope ?? "/";
  const checkInterval = options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Only reload for an update the user asked for; a first-install `claim()` must not
    // reload the page under the cashier's fingers.
    if (!applying) return;
    applying = false;
    if (typeof location !== "undefined") location.reload();
  });

  void navigator.serviceWorker
    .register(swUrl, { scope, type: "classic" })
    .then((registration) => {
      setState({ registration, registered: true, error: null });
      trackWaiting(registration);

      if (checkInterval > 0) {
        intervalId = setInterval(() => void checkForUpdate(), checkInterval);
      }
      // Cheap extra checks: coming back to the app, and coming back online.
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") void checkForUpdate();
        });
      }
      if (typeof window !== "undefined") {
        window.addEventListener("online", () => void checkForUpdate());
      }
    })
    .catch((error: unknown) => {
      setState({ registered: true, error: error instanceof Error ? error : new Error(String(error)) });
    });
}

/** Ask the browser to re-fetch the worker script. Resolves even when offline. */
export async function checkForUpdate(): Promise<void> {
  const registration = state.registration;
  if (!registration) return;
  try {
    await registration.update();
  } catch {
    /* offline or throttled — try again on the next tick */
  }
}

/**
 * Applies a waiting update: tells the waiting worker to activate, then reloads once it
 * takes control. The shell must only call this at a safe moment (no open cart, no
 * tender in progress) — F-701.
 */
export async function applyUpdate(): Promise<boolean> {
  const registration = state.registration;
  const waiting = registration?.waiting;
  if (!waiting) return false;
  applying = true;
  setState({ updateReady: false });
  waiting.postMessage({ type: SKIP_WAITING_MESSAGE });
  return true;
}

/** Test seam: reset module state between tests. */
export function __resetRegistration(): void {
  if (intervalId !== null) clearInterval(intervalId);
  intervalId = null;
  started = false;
  applying = false;
  listeners.clear();
  state = { updateReady: false, registration: null, registered: false, error: null };
}

/** Test seam: inject a registration (used by `usePwa` tests and by C1's storybook-ish fakes). */
export function __setRegistrationForTest(registration: ServiceWorkerRegistration | null, updateReady = false): void {
  setState({ registration, updateReady, registered: true });
}
